import React from 'react';
import type { GeneratedMission } from '@bid/shared-types';

interface MissionDisplayProps {
  mission: GeneratedMission;
  onSave?: () => void;
}

export const MissionDisplay: React.FC<MissionDisplayProps> = ({ mission, onSave }): JSX.Element => {
  const cardStyle: React.CSSProperties = {
    backgroundColor: '#16162a',
    borderRadius: '12px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, transparent 100%)',
  };

  const targetStyle: React.CSSProperties = {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#d4af37',
    margin: 0,
    fontFamily: 'Cinzel, serif',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#d4af37',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    marginBottom: '12px',
  };

  const infoGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  };

  const infoItemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#e5e7eb',
    fontWeight: 500,
  };

  const tagContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  };

  const tagStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#0f0f1e',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    borderRadius: '20px',
    fontSize: '13px',
    color: '#e5e7eb',
  };

  const listStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  };

  const listItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: '#0f0f1e',
    borderRadius: '6px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const warningIconStyle: React.CSSProperties = {
    color: '#d4af37',
    fontSize: '16px',
    flexShrink: 0,
    marginTop: '2px',
  };

  const listTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#e5e7eb',
    lineHeight: 1.5,
  };

  const saveButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: '#d4af37',
    color: '#0f0f1e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '8px',
    boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <h2 style={targetStyle}>{mission.target}</h2>
      </div>

      <div style={contentStyle}>
        {/* Basic Info */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Mission Details</h3>
          <div style={infoGridStyle}>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Score Type</span>
              <span style={infoValueStyle}>{mission.scoreType}</span>
            </div>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Heat Level</span>
              <span style={infoValueStyle}>{mission.heatLevel}</span>
            </div>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Location</span>
              <span style={infoValueStyle}>{mission.location}</span>
            </div>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Coin Reward</span>
              <span style={infoValueStyle}>{mission.rewards.coin}</span>
            </div>
          </div>
        </div>

        {/* Faction Involvement */}
        {mission.factionInvolvement.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Faction Involvement</h3>
            <div style={tagContainerStyle}>
              {mission.factionInvolvement.map((faction, index) => (
                <span key={index} style={tagStyle}>
                  {faction}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Complications */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Complications</h3>
          <div style={listStyle}>
            {mission.complications.map((complication, index) => (
              <div key={index} style={listItemStyle}>
                <span style={warningIconStyle}>⚠</span>
                <span style={listTextStyle}>{complication}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Rewards */}
        {mission.rewards.other.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Additional Rewards</h3>
            <div style={tagContainerStyle}>
              {mission.rewards.other.map((reward, index) => (
                <span key={index} style={tagStyle}>
                  {reward}
                </span>
              ))}
            </div>
          </div>
        )}

        {onSave && (
          <button
            onClick={onSave}
            style={saveButtonStyle}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#e5c158';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#d4af37';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
            }}
          >
            Save Mission
          </button>
        )}
      </div>
    </div>
  );
};
